import { getUuid } from '@sibe/utilities/common';
import type { HsRenderObjects } from '../engine-new/filtering/render-objects.class';
import type { HsCamera } from '../engine-new/objects/cameras/camera.class';
import type { HsGeometry } from '../graphic-engine-new/geometry/geometry.interface';
import { HsIndexedGeometry } from '../graphic-engine-new/geometry/indexed-geometry.class';
import { HsSpriteGeometry } from '../graphic-engine-new/geometry/sprite-geometry.class';
import { HsTexturedIndexedGeometry } from '../graphic-engine-new/geometry/textured-indexed-geometry.class';
import { HsMatrix3 } from '../math-new/objects/matrix3.class';
import { HsMatrix4 } from '../math-new/objects/matrix4.class';
import { HsVector3 } from '../math-new/objects/vector3.class';
import type { HsGeometryData } from '../tools-new/geometry-utils/geometry-data.class';
import { hsIndexGeometry } from '../tools-new/geometry-utils/index-geometry.function';
import type { HsIndexedGeometryData } from '../tools-new/geometry-utils/indexed-geometry-data.class';
import { hsMakeIndexedGeometry } from '../tools-new/geometry-utils/make-indexed-geometry.function';
import { hsGenerateCubeGeometryData } from '../tools-new/primitives/generate-cube-geometry-data.function';
import { HsGraphicConstants } from './constants/graphic-constants.namespace';
import type { HsGraphics } from './graphics.class';
import { HsDefaultMaterial } from './materials/default-material.class';
import type { HsMaterial } from './materials/material.class';
import { HsTextureMaterial } from './materials/texture-material.class';
import { HsGraphicCollection } from './objects/graphic-collection.class';
import type { HsGraphicLayer } from './objects/graphic-layer.class';
import type { HsLayerPostProcess } from './types/layer-post-process.interface';
import { HsGraphicLines } from './objects/graphic-lines.class';
import { HsGraphicObject } from './objects/graphic-object.class';
import { HsGraphicPoints } from './objects/graphic-points.class';
import { HsGraphicSprite } from './objects/graphic-sprite.class';
import { HsSpriteConstants } from './objects/sprite-constants-class';
import type { HsDefaultShader } from './shaders/classes/default-shader.class';
import type { HsShader2D } from './shaders/classes/shader-2d.class';
import { HsShaderData } from './shaders/classes/shader-data.class';
import { HsShader } from './shaders/classes/shader.class';
import { HsShaderObjectType } from './shaders/enums/shader-object-type.enum';
import { HsBoxHelper } from '../tools-new/helpers/box-helper.class';
import { HsBox } from '../math-new/shapes/box.class';
import { HsColor } from '../math-new/objects/color.class';
import { HsMeshBatchRenderer, type HsMeshBatch } from './mesh-batch.class';
import type { HsHypersceneTypes } from '../math-new/types/hyperscene-types.type';
import { HsVertexArrayCache } from './vertex-array-cache.class';

const defaultShader: HsShader = new HsShaderData(HsShaderObjectType.single).addColor().addDepth().makeShader();

interface AdaptiveDrawState {
  timeMs: number;
  objectsTotal: number;
  completeTexture: boolean;
  deferredPreviews: number;
  probeCountdown: number;
  passTimeMs?: number;
  nextIndex?: number;
  passObjectsTotal?: number;
}

interface BatchPreparation {
  opaque: HsGraphicObject[];
  transparent: HsGraphicObject[];
  index: number;
  swept: boolean;
}

export interface HsRenderFrameStats {
  drawCalls: number;
  instancedDrawCalls: number;
  instancedInstances: number;
  multiDrawCalls?: number;
  multiDrawObjects?: number;
  submittedTriangles: number;
  framebufferTransfers: number;
}

/**
 * Handles s render.
 */
/** Alpha written by point cloud draws when the layer post process masks them; the post process tests for below 0.75. */
const HS_POINT_PIXEL_TAG_ALPHA: number = 0.5;

export class HsRender {
  public frameTimeForDistributedRendering: number = 16.6;
  public distributedRenderingInterpolation: number = 0.95;
  public distributedRenderingInertiaFrames: number = 3;
  public readonly clearColor: HsColor = new HsColor(1, 1, 1);

  public downsampleRate: number = 1;
  public pointCloudSamplingStep: number = 1;
  /** Multiplies every point cloud's point size; hosts enlarge points while a frame draws fewer of them. */
  public pointCloudSizeScale: number = 1;
  public meshDrawBatchingEnabled: boolean = false;
  public meshBatchingRevision: number = 0;
  public meshBatchPreparationEnabled: boolean = false;
  public meshBatchDataReuseEnabled: boolean = false;
  public distributedCostPredictionEnabled: boolean = false;
  public meshBatchPreparationPending: boolean = false;
  public readonly frameStats: Required<HsRenderFrameStats> = {
    drawCalls: 0,
    instancedDrawCalls: 0,
    instancedInstances: 0,
    multiDrawCalls: 0,
    multiDrawObjects: 0,
    submittedTriangles: 0,
    framebufferTransfers: 0,
  };

  private currentShader: HsShader = defaultShader;
  private currentGeometry: HsGeometry | null = null;
  // Shader whose attribute arrays are enabled on the default vertex array; attributes are enabled lazily there.
  private defaultAttributesShader: HsShader | null = null;
  private readonly vertexArrays: HsVertexArrayCache = new HsVertexArrayCache();
  private vertexArrayBound: boolean = false;
  private passStartedAt: number = 0;
  private meshBatches: HsMeshBatchRenderer | null = null;
  private batchPreparation: WeakMap<HsRenderObjects<HsGraphicObject, HsGraphicLayer>, BatchPreparation> = new WeakMap();
  private adaptiveDrawStates: WeakMap<HsRenderObjects<HsGraphicObject, HsGraphicLayer>, AdaptiveDrawState> =
    new WeakMap();

  // Camera uniforms are identical for every program in a pass; programs keep them until the camera moves.
  private cameraUniformsRevision: number = -1;
  private cameraUniformsStale: boolean = true;
  private readonly cameraProjection: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private readonly cameraPosition: HsHypersceneTypes.Array3 = [0, 0, 0];
  private cameraLogDepth: number = 0;

  private defaultLayerMaterial: HsTextureMaterial<HsShader2D> | null = null;
  private layerGeometry: HsSpriteGeometry | null = null;

  private cullingCube: HsGraphicObject | null = null;
  private cullingMaterial: HsDefaultMaterial<HsDefaultShader> | null = null;
  private defaultMaterial: HsDefaultMaterial<HsShader> | null = null;
  private lineBox: HsGraphicLines | null = null;

  private tagPointPixels: boolean = false;

  public static readonly minDownsampleRate: number = 1;
  public static readonly maxDownsampleRate: number = 32;

  static #nextCameraUniformsRevision: number = 0;
  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp2: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();

  public init(graphics: HsGraphics, defaultLayerMaterial: HsTextureMaterial<HsShader2D>): void {
    this.clearMeshBatches();
    graphics.gl.getExtension('EXT_color_buffer_float');
    graphics.gl.enable(graphics.gl.BLEND);
    graphics.gl.enable(graphics.gl.DEPTH_TEST);
    graphics.gl.depthFunc(graphics.gl.LESS);
    graphics.gl.depthMask(true);
    graphics.gl.depthRange(0, 1);
    graphics.gl.frontFace(graphics.gl.CW);
    graphics.gl.enable(graphics.gl.POLYGON_OFFSET_FILL);
    graphics.gl.polygonOffset(0, 0);
    graphics.gl.blendEquationSeparate(graphics.gl.FUNC_ADD, graphics.gl.FUNC_ADD);
    graphics.gl.blendFuncSeparate(
      graphics.gl.SRC_ALPHA,
      graphics.gl.ONE_MINUS_SRC_ALPHA,
      graphics.gl.ONE,
      graphics.gl.ONE_MINUS_SRC_ALPHA
    );
    graphics.gl.disable(graphics.gl.CULL_FACE);
    this.defaultLayerMaterial = defaultLayerMaterial;
    this.layerGeometry = HsSpriteConstants.makeSpriteGeometry(graphics);

    const cullingGeometryData: HsGeometryData = hsGenerateCubeGeometryData();
    const cullingIndexedGeometryData: HsIndexedGeometryData = hsIndexGeometry(cullingGeometryData);
    const cullingGeometry: HsIndexedGeometry | null = hsMakeIndexedGeometry(graphics, cullingIndexedGeometryData);
    if (cullingGeometry === null) {
      return;
    }
    const cullingShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    const cullingShader: HsDefaultShader = cullingShaderData.makeShader();
    this.cullingCube = new HsGraphicObject(getUuid(), '', cullingGeometry, defaultLayerMaterial);
    this.cullingMaterial = new HsTextureMaterial(graphics.gl, cullingShader);
    this.defaultMaterial = new HsDefaultMaterial(graphics.gl, defaultShader);
    const boxHelper: HsBoxHelper = new HsBoxHelper(graphics, HsBox.makeUnitCube(), HsColor.makeRed());
    this.lineBox = boxHelper.makeLines();
  }

  public draw(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    culling: boolean = false
  ): void {
    this.frameStats.drawCalls = 0;
    this.frameStats.instancedDrawCalls = 0;
    this.frameStats.instancedInstances = 0;
    this.frameStats.multiDrawCalls = 0;
    this.frameStats.multiDrawObjects = 0;
    this.frameStats.submittedTriangles = 0;
    this.frameStats.framebufferTransfers = 0;
    if (renderObjects.sortedObjects.length + renderObjects.unsortedObjects.length === 0) {
      return;
    }
    const drawAsCube: boolean = renderObjects.layer.tags.has('drawAsCube');
    this.beginPass(gl);
    this.beginMeshBatchDraw();
    for (let i: number = 0; i < renderObjects.unsortedObjects.length; ) {
      i += this.renderObjectsAt(gl, camera, renderObjects.unsortedObjects, i, false, culling, drawAsCube);
    }
    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    for (let i: number = 0; i < renderObjects.sortedObjects.length; ) {
      i += this.renderObjectsAt(gl, camera, renderObjects.sortedObjects, i, true, false, drawAsCube);
    }
    this.finishFrame(gl);
  }

  public drawDistributed(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    objectIndex: number = 0,
    limitObjectCount: boolean = !this.meshDrawBatchingEnabled,
    adaptiveFrameDeadline: number = 0
  ): number {
    const objectsTotal: number = renderObjects.sortedObjects.length + renderObjects.unsortedObjects.length;
    if (objectsTotal === 0 || objectIndex >= objectsTotal) {
      return objectsTotal;
    }
    const frameBudgetMs: number = Math.max(1, this.frameTimeForDistributedRendering);
    const adaptive: boolean = Number.isFinite(adaptiveFrameDeadline) && adaptiveFrameDeadline > 0;
    const initialObjectIndex: number = objectIndex;
    limitObjectCount = limitObjectCount && !adaptive;
    const previousTimePerObject: number = Math.max(0, renderObjects.timePerObject);
    const inertiaFrames: number = Math.max(0, Math.min(50, Math.floor(this.distributedRenderingInertiaFrames)));
    const useInertia: boolean = limitObjectCount && renderObjects.distributedFrameInertiaRemaining > 0;
    const measuredObjectsToDraw: number =
      previousTimePerObject > 0 ? Math.max(1, Math.ceil(frameBudgetMs / previousTimePerObject)) : Infinity;
    const objectsToDraw: number = limitObjectCount
      ? useInertia
        ? renderObjects.distributedObjectsPerFrame
        : measuredObjectsToDraw
      : Infinity;
    const time: number = performance.now();
    const maximumBudgetMs: number = Math.max(frameBudgetMs, Math.min(frameBudgetMs * 2, adaptiveFrameDeadline - time));
    let learned: AdaptiveDrawState | undefined = this.adaptiveDrawStates.get(renderObjects);
    if (adaptive && initialObjectIndex === 0) {
      if (learned === undefined) {
        learned = {
          timeMs: Infinity,
          objectsTotal,
          completeTexture: false,
          deferredPreviews: 0,
          probeCountdown: 0,
        };
        this.adaptiveDrawStates.set(renderObjects, learned);
      }
      learned.probeCountdown = Math.max(0, learned.probeCountdown - 1);
    }
    const learnedTimeMs: number =
      learned !== undefined &&
      objectsTotal >= learned.objectsTotal * 0.75 &&
      objectsTotal <= learned.objectsTotal * 1.25
        ? (learned.timeMs * objectsTotal * 1.25) / learned.objectsTotal
        : Infinity;
    let activeBudgetMs: number = frameBudgetMs;
    let objectsRendered: number = 0;
    const drawAsCube: boolean = renderObjects.layer.tags.has('drawAsCube');
    this.beginPass(gl);
    this.beginMeshBatchDraw();
    const shouldFinishChunk = (): boolean => {
      const elapsed: number = performance.now() - time;
      if (objectsRendered >= objectsToDraw) {
        return true;
      }
      if (elapsed < activeBudgetMs) {
        return false;
      }
      // Finish a nearly complete new view only when it fits this frame's remaining CPU time.
      if (
        adaptive &&
        initialObjectIndex === 0 &&
        activeBudgetMs === frameBudgetMs &&
        maximumBudgetMs > frameBudgetMs &&
        objectsRendered > 0 &&
        elapsed < maximumBudgetMs &&
        (this.distributedCostPredictionEnabled && Number.isFinite(learnedTimeMs)
          ? learnedTimeMs <= maximumBudgetMs ||
            (objectsRendered >= (objectsTotal * frameBudgetMs) / maximumBudgetMs && learned?.probeCountdown === 0)
          : Math.min((elapsed * objectsTotal) / objectsRendered, learnedTimeMs) <= maximumBudgetMs ||
            (objectsRendered >= objectsTotal / 3 && learned?.probeCountdown === 0))
      ) {
        if (learned !== undefined) {
          learned.probeCountdown = 8;
        }
        activeBudgetMs = maximumBudgetMs;
        return elapsed >= activeBudgetMs;
      }
      return true;
    };
    const updateTiming = (): void => {
      if (!useInertia) {
        const timePerObject: number = (performance.now() - time) / Math.max(objectsRendered, 1);
        renderObjects.timePerObject = limitObjectCount
          ? previousTimePerObject * this.distributedRenderingInterpolation +
            timePerObject * (1 - this.distributedRenderingInterpolation)
          : timePerObject;
      }
    };
    const finishDistributedFrame = (calibrate: boolean = true): void => {
      if (useInertia) {
        renderObjects.distributedFrameInertiaRemaining = Math.max(
          0,
          renderObjects.distributedFrameInertiaRemaining - 1
        );
        return;
      }
      if (calibrate) {
        renderObjects.distributedObjectsPerFrame = objectsRendered;
        renderObjects.distributedFrameInertiaRemaining = inertiaFrames;
      }
    };
    if (objectIndex < renderObjects.unsortedObjects.length) {
      for (let i: number = objectIndex; i < renderObjects.unsortedObjects.length; ) {
        const count: number = this.renderObjectsAt(
          gl,
          camera,
          renderObjects.unsortedObjects,
          i,
          false,
          false,
          drawAsCube,
          objectsToDraw - objectsRendered
        );
        i += count;
        objectsRendered += count;
        if (shouldFinishChunk()) {
          this.finishFrame(gl);
          updateTiming();
          finishDistributedFrame();
          return i;
        }
      }
      objectIndex = renderObjects.unsortedObjects.length;
    }
    const transparentObjectIndex: number = objectIndex - renderObjects.unsortedObjects.length;
    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    for (let i: number = transparentObjectIndex; i < renderObjects.sortedObjects.length; ) {
      const count: number = this.renderObjectsAt(
        gl,
        camera,
        renderObjects.sortedObjects,
        i,
        true,
        false,
        drawAsCube,
        objectsToDraw - objectsRendered
      );
      i += count;
      objectsRendered += count;
      if (shouldFinishChunk()) {
        this.finishFrame(gl);
        updateTiming();
        finishDistributedFrame();
        return renderObjects.unsortedObjects.length + i;
      }
    }
    this.finishFrame(gl);
    // A full single-frame pass is a valid sample; a short tail is not.
    const calibrateCompletion: boolean =
      initialObjectIndex === 0 || !this.meshDrawBatchingEnabled || previousTimePerObject <= 0;
    if (!useInertia && calibrateCompletion) {
      renderObjects.timePerObject = (performance.now() - time) / Math.max(objectsRendered, 1);
    }
    finishDistributedFrame(calibrateCompletion);
    return renderObjects.sortedObjects.length + renderObjects.unsortedObjects.length;
  }

  public drawBackground(gl: WebGL2RenderingContext, shader: HsShader2D, camera: HsCamera): void {
    if (this.layerGeometry === null) {
      return;
    }

    const rotation: HsMatrix3 = HsMatrix3.makeIdentityMatrix().setFromMatrix4(camera.worldMatrix.values);
    shader.uniformMatrix3fv(gl, 'u_rotation', rotation.values);
    shader.useProgram(gl);
    shader.enable(gl);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.layerGeometry.buffer);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.layerGeometry.indexBuffer);
    const positionsLocation: number = this.currentShader.getAttribute('a_positions');
    gl.vertexAttribPointer(
      positionsLocation,
      HsGraphicConstants.FLOATS_IN_VEC2,
      gl.FLOAT,
      false,
      HsGraphicConstants.FLOAT32_BYTES * HsGraphicConstants.FLOATS_IN_VEC2,
      0
    );
    gl.drawElements(gl.TRIANGLES, this.layerGeometry.indexArrayLength, gl.UNSIGNED_INT, 0);
    shader.disable(gl);
  }

  public clearMeshBatches(): void {
    this.meshBatches?.dispose();
    this.meshBatches = null;
    this.currentGeometry = null;
    this.adaptiveDrawStates = new WeakMap();
    this.batchPreparation = new WeakMap();
    this.meshBatchPreparationPending = false;
    this.meshBatchingRevision++;
  }

  public prepareMeshBatches(
    gl: WebGL2RenderingContext,
    objects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    budgetMs: number = 2
  ): boolean {
    if (!Number.isFinite(budgetMs) || budgetMs < 0) {
      throw new RangeError('Mesh batch preparation budget must be a finite non-negative number.');
    }
    if (!this.meshDrawBatchingEnabled || !this.meshBatchPreparationEnabled || objects.layer.tags.has('drawAsCube')) {
      this.meshBatchPreparationPending = false;
      return true;
    }
    this.meshBatches ??= new HsMeshBatchRenderer(gl);
    let state: BatchPreparation | undefined = this.batchPreparation.get(objects);
    if (
      state === undefined ||
      state.opaque !== objects.unsortedObjects ||
      state.transparent !== objects.sortedObjects
    ) {
      state = { opaque: objects.unsortedObjects, transparent: objects.sortedObjects, index: 0, swept: false };
      this.batchPreparation.set(objects, state);
      this.meshBatches.beginPreparationSweep();
    }
    const deadline: number = performance.now() + Math.max(0, budgetMs);
    const total: number = state.opaque.length + state.transparent.length;
    const revision: number = this.meshBatches.revision;
    while (state.index < total && performance.now() < deadline) {
      const transparent: boolean = state.index >= state.opaque.length;
      const list: HsGraphicObject[] = transparent ? state.transparent : state.opaque;
      const index: number = transparent ? state.index - state.opaque.length : state.index;
      const batch: HsMeshBatch | null = this.meshBatches.getBatch(list, index, transparent);
      if (batch !== null) {
        this.meshBatches.getBatchShader(batch, true);
      }
      state.index += batch?.count ?? 1;
    }
    this.currentGeometry = null;
    if (state.index >= total && !state.swept) {
      state.swept = true;
      if (this.meshBatches.releaseGeometryOutsideSweep(total)) {
        // The rebuilt atlas holds nothing yet; sweep the current lists again to repack them in render order.
        state = { opaque: objects.unsortedObjects, transparent: objects.sortedObjects, index: 0, swept: false };
        this.batchPreparation = new WeakMap([[objects, state]]);
        this.meshBatches.beginPreparationSweep();
      }
    }
    if (this.meshBatches.revision !== revision) {
      this.meshBatchingRevision++;
    }
    const complete: boolean = state.index >= total && this.meshBatches.preparationComplete(deadline);
    this.meshBatchPreparationPending = !complete;
    return complete;
  }

  public estimateMeshDrawCalls(
    objects: HsGraphicObject[],
    transparent: boolean
  ): Pick<HsRenderFrameStats, 'drawCalls' | 'instancedDrawCalls' | 'instancedInstances'> {
    const stats: Pick<HsRenderFrameStats, 'drawCalls' | 'instancedDrawCalls' | 'instancedInstances'> = {
      drawCalls: 0,
      instancedDrawCalls: 0,
      instancedInstances: 0,
    };
    for (let index: number = 0; index < objects.length; ) {
      const object: HsGraphicObject = objects[index];
      if (!object.visible || object.isDeleted || object.geometry.isDeleted || object.occluded) {
        index++;
        continue;
      }
      const batch: HsMeshBatch | null = this.meshDrawBatchingEnabled
        ? this.meshBatches?.getBatch(objects, index, transparent, undefined, false) ?? null
        : null;
      stats.drawCalls++;
      if (batch?.mode === 'instances') {
        stats.instancedDrawCalls++;
        stats.instancedInstances += batch.count;
      } else if (object instanceof HsGraphicCollection) {
        stats.instancedDrawCalls++;
        stats.instancedInstances += object.getCount();
      }
      index += batch?.count ?? 1;
    }
    return stats;
  }

  public setRenderSize(gl: WebGL2RenderingContext, width: number, height: number, layers: HsGraphicLayer[]): void {
    width /= this.downsampleRate;
    height /= this.downsampleRate;
    gl.canvas.width = width;
    gl.canvas.height = height;
    gl.viewport(0, 0, width, height);
    layers.forEach((layer: HsGraphicLayer) => {
      layer.resizeFramebuffer(gl, width, height);
    });
  }

  public drawToFramebuffer(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    objectIndex: number = -1,
    checkCulling: boolean = false,
    presentDistributedProgress: boolean = false,
    adaptiveFrameDeadline: number = 0
  ): number {
    if (!this.meshBatchPreparationEnabled) {
      this.meshBatchPreparationPending = false;
    }
    this.frameStats.drawCalls = 0;
    this.frameStats.instancedDrawCalls = 0;
    this.frameStats.instancedInstances = 0;
    this.frameStats.multiDrawCalls = 0;
    this.frameStats.multiDrawObjects = 0;
    this.frameStats.submittedTriangles = 0;
    this.frameStats.framebufferTransfers = 0;
    const objectsTotal: number = renderObjects.sortedObjects.length + renderObjects.unsortedObjects.length;
    const initialObjectIndex: number = objectIndex;
    const adaptive: boolean = Number.isFinite(adaptiveFrameDeadline) && adaptiveFrameDeadline > 0;
    this.tagPointPixels = renderObjects.layer.postProcess?.maskPointClouds === true;
    const hadPresentedFrame: boolean = renderObjects.layer.hasPresentedFrame;
    const reusePresentedFrame: boolean =
      objectIndex > 0 && objectIndex >= objectsTotal && renderObjects.layer.hasPresentedFrame;
    if (!reusePresentedFrame) {
      if (!adaptive) {
        this.adaptiveDrawStates.delete(renderObjects);
      }
      const drawStartedAt: number = adaptive ? performance.now() : 0;
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, renderObjects.layer.samplingFramebuffer);
      gl.enable(gl.BLEND);
      gl.enable(gl.DEPTH_TEST);
      if (objectIndex <= 0) {
        renderObjects.layer.presentDistributedProgress =
          presentDistributedProgress || !renderObjects.layer.hasPresentedFrame;
        renderObjects.layer.hasPresentedFrame = false;
        this.clearViewport(gl, renderObjects.layer.transparentBackground ? 0 : 1);
      } else if (presentDistributedProgress) {
        renderObjects.layer.presentDistributedProgress = true;
      }
      gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
      if (objectIndex === -1) {
        this.draw(gl, camera, renderObjects, checkCulling);
      } else {
        objectIndex = this.drawDistributed(
          gl,
          camera,
          renderObjects,
          objectIndex,
          !this.meshDrawBatchingEnabled || presentDistributedProgress,
          adaptiveFrameDeadline
        );
      }
      const isDistributedRenderComplete: boolean = objectIndex === -1 || objectIndex >= objectsTotal;
      const deferPreview: boolean =
        adaptive &&
        this.deferAdaptivePreview(
          renderObjects,
          initialObjectIndex,
          isDistributedRenderComplete,
          performance.now() - drawStartedAt,
          hadPresentedFrame,
          objectIndex
        );
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
      if (isDistributedRenderComplete || (renderObjects.layer.presentDistributedProgress && !deferPreview)) {
        renderObjects.layer.transferToTexture(gl);
        this.frameStats.framebufferTransfers++;
        renderObjects.layer.hasPresentedFrame = true;
        this.runLayerPostProcess(gl, camera, renderObjects.layer, true);
      } else if (deferPreview && hadPresentedFrame) {
        renderObjects.layer.hasPresentedFrame = true;
      }
    }

    if (this.defaultLayerMaterial === null) {
      return objectIndex;
    }

    if (reusePresentedFrame) {
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
      gl.enable(gl.BLEND);
      this.runLayerPostProcess(gl, camera, renderObjects.layer, false);
    }
    const presentedTexture: WebGLTexture | null =
      renderObjects.layer.postProcess?.outputTexture ?? renderObjects.layer.texture;
    this.defaultLayerMaterial.getShader().useProgram(gl);
    this.defaultLayerMaterial.texture = presentedTexture;

    const size: number = renderObjects.layer.renderSize;
    const layerX: number = renderObjects.layer.getLeft() / gl.canvas.width;
    const layerY: number = renderObjects.layer.getTop() / gl.canvas.height;
    this.defaultLayerMaterial.getShader().uniform4f(gl, 'u_transform', size, size, layerX, layerY);

    if (this.layerGeometry === null) {
      return objectIndex;
    }

    const offset: number = this.layerGeometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;

    this.defaultLayerMaterial.getShader().enable(gl);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.layerGeometry.indexBuffer);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.layerGeometry.buffer);

    const dimensions: number = HsGraphicConstants.FLOAT32_BYTES * HsGraphicConstants.FLOATS_IN_VEC2;

    const positionLocation: number = this.defaultLayerMaterial.getShader().getAttribute('a_position');
    gl.vertexAttribPointer(positionLocation, HsGraphicConstants.FLOATS_IN_VEC2, gl.FLOAT, false, dimensions, 0);
    const uvsLocation: number = this.defaultLayerMaterial.getShader().getAttribute('a_texcoord');
    gl.vertexAttribPointer(uvsLocation, HsGraphicConstants.FLOATS_IN_VEC2, gl.FLOAT, false, dimensions, offset);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, presentedTexture);
    this.defaultLayerMaterial.getShader().uniform1i(gl, 'u_texture', 0);

    gl.disable(gl.DEPTH_TEST);

    gl.drawElements(gl.TRIANGLES, this.layerGeometry.indexArrayLength, gl.UNSIGNED_INT, 0);

    gl.enable(gl.DEPTH_TEST);

    this.defaultLayerMaterial.getShader().disable(gl);

    return objectIndex;
  }

  public disableShader(gl: WebGL2RenderingContext): void {
    this.useDefaultVertexArray(gl, false);
    HsShader.disableAllAttributes(gl);
    this.defaultAttributesShader = null;
    this.currentShader = defaultShader;
    this.currentGeometry = null;
  }

  /**
   * Runs the layer post process on the freshly resolved frame, or on a cached one when only its quality changed.
   * The post process is skipped for layers without one, so the baseline path costs a single null check.
   */
  private runLayerPostProcess(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    layer: HsGraphicLayer,
    frameChanged: boolean
  ): void {
    const postProcess: HsLayerPostProcess | null | undefined = layer.postProcess;
    if (!postProcess || (!frameChanged && !postProcess.isStale())) {
      return;
    }
    postProcess.run(gl, camera, layer);
    this.disableShader(gl);
  }

  /** Number of vertex array objects currently cached for individually drawn meshes and collections. */
  public get vertexArrayCount(): number {
    return this.vertexArrays.size;
  }

  private drawCullingBox(gl: WebGL2RenderingContext, camera: HsCamera, renderObject: HsGraphicObject): void {
    if (this.cullingCube === null || this.cullingMaterial === null) {
      return;
    }
    const cubeGeometry: HsGeometry = this.cullingCube.geometry;
    if (!(cubeGeometry instanceof HsIndexedGeometry)) {
      return;
    }
    this.setShader(gl, camera, this.cullingMaterial.getShader());
    this.useDefaultVertexArray(gl);
    this.setGeometry(gl, this.cullingCube.geometry);
    if (renderObject.query === null) {
      renderObject.query = gl.createQuery();
    }
    if (renderObject.query === null) return;
    this.cullingCube.worldMatrix.copy(renderObject.worldMatrix);
    HsRender.#matrix4Temp2.copy(this.cullingCube.worldMatrix);
    HsRender.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRender.#matrix4Temp2.subtractPosition(HsRender.#vector3Temp1.values);
    this.currentShader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRender.#matrix4Temp2.values);
    gl.colorMask(false, false, false, false);
    gl.depthMask(false);
    if (renderObject.queryInProgress) {
      const resultAvailable: boolean = gl.getQueryParameter(renderObject.query, gl.QUERY_RESULT_AVAILABLE);
      if (resultAvailable) {
        renderObject.occluded = !Boolean(gl.getQueryParameter(renderObject.query, gl.QUERY_RESULT));
        renderObject.queryInProgress = false;
      }
    }
    if (!renderObject.queryInProgress) {
      gl.beginQuery(gl.ANY_SAMPLES_PASSED_CONSERVATIVE, renderObject.query);
      gl.drawElements(gl.TRIANGLES, cubeGeometry.indexArrayLength, gl.UNSIGNED_INT, 0);
      gl.endQuery(gl.ANY_SAMPLES_PASSED_CONSERVATIVE);
      renderObject.queryInProgress = true;
    }
    gl.colorMask(true, true, true, true);
    gl.depthMask(true);
  }

  private beginMeshBatchDraw(): void {
    if (this.meshBatches === null) {
      return;
    }
    // Preparing hosts repack only while stationary, so they rebuild the atlas in prepareMeshBatches instead.
    if (!this.meshBatchPreparationEnabled && this.meshBatches.releaseUnusedGeometry()) {
      this.currentGeometry = null;
      this.meshBatchingRevision++;
    }
    this.meshBatches.beginDraw();
  }

  private beginPass(gl: WebGL2RenderingContext): void {
    this.cameraUniformsStale = true;
    this.passStartedAt = performance.now();
    this.vertexArrays.sweep(gl, this.passStartedAt);
  }

  private finishFrame(gl: WebGL2RenderingContext): void {
    gl.depthMask(true);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    this.disableShader(gl);
  }

  private deferAdaptivePreview(
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    initialObjectIndex: number,
    complete: boolean,
    timeMs: number,
    hadPresentedFrame: boolean,
    endIndex: number
  ): boolean {
    const objectsTotal: number = renderObjects.unsortedObjects.length + renderObjects.sortedObjects.length;
    if (objectsTotal === 0) {
      this.adaptiveDrawStates.delete(renderObjects);
      return false;
    }
    let learned: AdaptiveDrawState | undefined = this.adaptiveDrawStates.get(renderObjects);
    if (learned !== undefined && this.distributedCostPredictionEnabled) {
      learned.passTimeMs =
        initialObjectIndex <= 0
          ? timeMs
          : learned.nextIndex === initialObjectIndex &&
            learned.passObjectsTotal === objectsTotal &&
            learned.passTimeMs !== undefined
          ? learned.passTimeMs + timeMs
          : undefined;
      learned.nextIndex = endIndex;
      learned.passObjectsTotal = objectsTotal;
    }
    if (complete && initialObjectIndex <= 0) {
      learned = { timeMs, objectsTotal, completeTexture: true, deferredPreviews: 0, probeCountdown: 0 };
      this.adaptiveDrawStates.set(renderObjects, learned);
    }
    if (learned === undefined) {
      return false;
    }
    if (complete) {
      if (this.distributedCostPredictionEnabled && initialObjectIndex > 0 && learned.passTimeMs !== undefined) {
        learned.timeMs = learned.passTimeMs;
        learned.objectsTotal = objectsTotal;
      }
      learned.completeTexture = true;
      learned.deferredPreviews = 0;
      return false;
    }
    if (
      hadPresentedFrame &&
      renderObjects.layer.presentDistributedProgress &&
      initialObjectIndex === 0 &&
      learned.completeTexture &&
      learned.deferredPreviews === 0 &&
      objectsTotal >= learned.objectsTotal * 0.75 &&
      objectsTotal <= learned.objectsTotal * 1.25 &&
      (learned.timeMs * objectsTotal * 1.25) / learned.objectsTotal <=
        Math.max(1, this.frameTimeForDistributedRendering) * 2
    ) {
      learned.deferredPreviews++;
      return true;
    }
    if (renderObjects.layer.presentDistributedProgress) {
      learned.completeTexture = false;
      learned.deferredPreviews = 0;
      if (initialObjectIndex === 0) {
        learned.timeMs = this.distributedCostPredictionEnabled ? Math.max(learned.timeMs, timeMs) : Infinity;
      }
    }
    return false;
  }

  private renderObjectsAt(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    objects: HsGraphicObject[],
    index: number,
    transparent: boolean,
    culling: boolean,
    drawAsCube: boolean,
    limit: number = Infinity
  ): number {
    if (!this.meshDrawBatchingEnabled && this.meshBatches !== null) {
      this.clearMeshBatches();
    }
    if (this.meshDrawBatchingEnabled && !culling && !drawAsCube) {
      // Batch lookup may allocate packed index buffers, which must not land in an object's vertex array.
      this.useDefaultVertexArray(gl, false);
      this.meshBatches ??= new HsMeshBatchRenderer(gl);
      this.meshBatches.reuseTransformData = this.meshBatchDataReuseEnabled;
      const previousRevision: number = this.meshBatches.revision;
      const batch: HsMeshBatch | null = this.meshBatches.getBatch(
        objects,
        index,
        transparent,
        limit,
        !this.meshBatchPreparationEnabled
      );
      if (this.meshBatches.revision !== previousRevision) {
        this.currentGeometry = null;
        this.meshBatchingRevision++;
      }
      if (batch !== null) {
        const shader: HsDefaultShader | null = this.meshBatchPreparationEnabled
          ? this.meshBatches.getPreparedBatchShader(batch)
          : this.meshBatches.getBatchShader(batch);
        if (shader === null) {
          this.renderObject(gl, camera, objects[index], culling, drawAsCube);
          return 1;
        }
        this.setShader(gl, camera, shader);
        this.useDefaultVertexArray(gl);
        shader.resetInstancedAttributes(gl);
        const triangles: number | null = this.meshBatches.draw(batch, objects, index, camera);
        if (triangles === null) {
          this.renderObject(gl, camera, objects[index], culling, drawAsCube);
          return 1;
        }
        this.frameStats.submittedTriangles += triangles;
        this.frameStats.drawCalls++;
        if (batch.mode === 'instances') {
          this.frameStats.instancedDrawCalls++;
          this.frameStats.instancedInstances += batch.count;
        } else {
          this.frameStats.multiDrawCalls++;
          this.frameStats.multiDrawObjects += batch.count;
        }
        this.currentGeometry = null;
        return batch.count;
      }
    }
    this.renderObject(gl, camera, objects[index], culling, drawAsCube);
    return 1;
  }

  private setShader(gl: WebGL2RenderingContext, camera: HsCamera, shader: HsShader): boolean {
    if (shader.uuid === this.currentShader.uuid) {
      return false;
    }
    this.currentShader = shader;
    shader.useProgram(gl);
    if (this.cameraUniformsStale) {
      this.cameraUniformsStale = false;
      this.updateCameraUniforms(camera);
    }
    if (shader.cameraUniformsRevision === this.cameraUniformsRevision) {
      return true;
    }
    shader.cameraUniformsRevision = this.cameraUniformsRevision;
    shader.uniformMatrix4fv(gl, 'u_projectionMatrix', this.cameraProjection.values);
    shader.uniform3fv(gl, 'u_cameraPosition', this.cameraPosition);
    shader.uniform1f(gl, 'u_logDepthBufFC', this.cameraLogDepth);
    shader.uniform1f(gl, 'u_alpha', 1);
    return true;
  }

  /** Starts a new camera uniform revision when the camera differs from the one the programs last received. */
  private updateCameraUniforms(camera: HsCamera): void {
    const projection: HsHypersceneTypes.Array16 = HsRender.#matrix4Temp1.multiplyMatrices(
      camera.projectionMatrix,
      camera.worldMatrixInverseWithZeroPosition
    ).values;
    const world: HsHypersceneTypes.Array16 = camera.worldMatrix.values;
    const logDepth: number = HsGraphicConstants.getLogDepth(camera.far);
    let changed: boolean =
      this.cameraUniformsRevision === -1 ||
      this.cameraPosition[0] !== world[12] ||
      this.cameraPosition[1] !== world[13] ||
      this.cameraPosition[2] !== world[14] ||
      this.cameraLogDepth !== logDepth;
    for (let i: number = 0; i < 16 && !changed; i++) {
      changed = this.cameraProjection.values[i] !== projection[i];
    }
    if (!changed) {
      return;
    }
    this.cameraProjection.copy(HsRender.#matrix4Temp1);
    this.cameraPosition[0] = world[12];
    this.cameraPosition[1] = world[13];
    this.cameraPosition[2] = world[14];
    this.cameraLogDepth = logDepth;
    this.cameraUniformsRevision = HsRender.#nextCameraUniformsRevision++;
  }

  /**
   * Prepares the default vertex array for a draw that specifies its own vertex pointers: unbinds an object's vertex
   * array and, when requested, enables exactly the current shader's attribute arrays.
   */
  private useDefaultVertexArray(gl: WebGL2RenderingContext, enableAttributes: boolean = true): void {
    if (this.vertexArrayBound) {
      gl.bindVertexArray(null);
      this.vertexArrayBound = false;
    }
    if (enableAttributes && this.defaultAttributesShader !== this.currentShader) {
      // enable() also disables the previous shader's other attributes, touching only locations that differ.
      this.currentShader.enable(gl);
      this.defaultAttributesShader = this.currentShader;
      this.currentGeometry = null;
    }
  }

  private bindVertexArray(
    gl: WebGL2RenderingContext,
    geometry: HsIndexedGeometry,
    collection: HsGraphicCollection | null
  ): boolean {
    if (!this.vertexArrays.bind(gl, this.currentShader, geometry, collection, this.passStartedAt)) {
      return false;
    }
    this.vertexArrayBound = true;
    // Creating a vertex array rebinds ARRAY_BUFFER, which default-array pointer updates rely on.
    this.currentGeometry = null;
    return true;
  }

  private setGeometry(gl: WebGL2RenderingContext, geometry: HsGeometry): void {
    if (geometry.isDeleted) {
      return;
    }
    this.currentGeometry = geometry;
    let dimensions: number = HsGraphicConstants.FLOATS_IN_VEC3;
    if (geometry instanceof HsIndexedGeometry) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, geometry.indexBuffer);
      if (geometry instanceof HsSpriteGeometry) {
        dimensions = HsGraphicConstants.FLOATS_IN_VEC2;
      }
    }
    const positionsLocation: number = this.currentShader.getAttribute('a_positions');
    gl.bindBuffer(gl.ARRAY_BUFFER, geometry.buffer);
    gl.vertexAttribPointer(
      positionsLocation,
      dimensions,
      gl.FLOAT,
      false,
      HsGraphicConstants.FLOAT32_BYTES * dimensions,
      0
    );
    const normalsLocation: number = this.currentShader.getAttribute('a_normals');
    if (normalsLocation !== -1) {
      const offset: number = geometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      gl.vertexAttribPointer(
        normalsLocation,
        dimensions,
        gl.FLOAT,
        true,
        HsGraphicConstants.FLOAT32_BYTES * dimensions,
        offset
      );
    }
  }

  private renderObject(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    object: HsGraphicObject,
    checkCulling: boolean = false,
    drawAsCube: boolean = true
  ): void {
    if (!object.visible || object.isDeleted) {
      return;
    }
    if (checkCulling) {
      this.drawCullingBox(gl, camera, object);
    }
    if (object.occluded) {
      return;
    }
    const geometry: HsGeometry = object.geometry;
    if (geometry.isDeleted) {
      return;
    }
    const shader: HsShader = object.material.getShader();
    this.setShader(gl, camera, shader);
    const collection: HsGraphicCollection | null =
      object instanceof HsGraphicCollection && geometry instanceof HsIndexedGeometry ? object : null;
    if (
      !drawAsCube &&
      geometry instanceof HsIndexedGeometry &&
      (collection !== null || HsRender.isPlainMesh(object)) &&
      this.bindVertexArray(gl, geometry, collection)
    ) {
      if (collection !== null) {
        this.renderIndexedCollection(gl, geometry, collection, true);
      } else {
        this.renderMeshObject(gl, camera, object, true);
      }
      return;
    }
    this.useDefaultVertexArray(gl);
    if (geometry.uuid !== this.currentGeometry?.uuid) {
      this.setGeometry(gl, geometry);
    }
    if (collection !== null && geometry instanceof HsIndexedGeometry) {
      this.renderIndexedCollection(gl, geometry, collection);
      return;
    }
    // Consecutive collections keep their instance divisors; any other draw needs per-vertex stepping again.
    this.currentShader.resetInstancedAttributes(gl);
    if (object instanceof HsGraphicSprite) {
      this.renderSprite(gl, object, camera);
      return;
    }
    if (object instanceof HsGraphicPoints) {
      this.renderPoints(gl, object, camera);
      return;
    }
    if (object instanceof HsGraphicLines) {
      this.renderLines(gl, object, camera);
      return;
    }
    if (drawAsCube) {
      this.renderLineCube(gl, camera, object);
      return;
    }
    this.renderMeshObject(gl, camera, object);
  }

  private renderMeshObject(
    gl: WebGL2RenderingContext,
    camera: HsCamera,
    object: HsGraphicObject,
    vertexArray: boolean = false
  ): void {
    const shader: HsShader = this.currentShader;
    const geometry: HsGeometry = object.geometry;
    shader.uniform3f(gl, 'u_color', object.material.color.r, object.material.color.g, object.material.color.b);
    HsRender.#matrix4Temp2.copy(object.worldMatrix);
    HsRender.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRender.#matrix4Temp2.subtractPosition(HsRender.#vector3Temp1.values);
    shader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRender.#matrix4Temp2.values);
    if (geometry instanceof HsTexturedIndexedGeometry) {
      this.renderTexturedObject(gl, geometry, object, vertexArray);
    } else if (geometry instanceof HsIndexedGeometry) {
      this.frameStats.drawCalls++;
      this.frameStats.submittedTriangles += geometry.indexArrayLength / 3;
      gl.drawElements(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0);
      return;
    } else {
      this.frameStats.drawCalls++;
      gl.drawArrays(gl.TRIANGLES, 0, geometry.positionArrayLength / 3);
    }
  }

  private renderLineCube(gl: WebGL2RenderingContext, camera: HsCamera, object: HsGraphicObject): void {
    if (this.lineBox === null || this.defaultMaterial === null) {
      return;
    }
    const shader: HsShader = this.defaultMaterial.getShader();
    this.setShader(gl, camera, shader);
    this.useDefaultVertexArray(gl);
    const geometry: HsGeometry = this.lineBox.geometry;
    this.setGeometry(gl, geometry);
    shader.uniform3f(gl, 'u_color', object.material.color.r, object.material.color.g, object.material.color.b);
    HsRender.#matrix4Temp2.copy(object.worldMatrix);
    HsRender.#matrix4Temp2.resetScale();
    HsRender.#matrix4Temp2.scale([
      object.boundingBox.max.x - object.boundingBox.min.x,
      object.boundingBox.max.y - object.boundingBox.min.y,
      object.boundingBox.max.z - object.boundingBox.min.z,
    ]);
    HsRender.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRender.#matrix4Temp2.subtractPosition(HsRender.#vector3Temp1.values);
    shader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRender.#matrix4Temp2.values);
    this.frameStats.drawCalls++;
    gl.drawArrays(gl.LINES, 0, geometry.positionArrayLength / 3);
  }

  private renderTexturedObject(
    gl: WebGL2RenderingContext,
    geometry: HsTexturedIndexedGeometry,
    object: HsGraphicObject,
    vertexArray: boolean
  ): void {
    const material: HsMaterial = object.material;
    if (!(material instanceof HsTextureMaterial)) {
      return;
    }
    const texture: WebGLTexture | null = material.texture;
    if (texture === null) {
      return;
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    const uvsLocation: number = this.currentShader.getAttribute('a_texcoord');
    if (uvsLocation === -1) {
      return;
    }
    this.currentShader.uniform4fv(gl, 'u_textureRect', material.textureRect);
    if (!vertexArray) {
      const firstOffset: number = geometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      const secondOffset: number = geometry.normalArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      const offset: number = firstOffset + secondOffset;
      gl.vertexAttribPointer(uvsLocation, 2, gl.FLOAT, false, HsGraphicConstants.XY_POINT_BYTES, offset);
    }
    this.frameStats.drawCalls++;
    this.frameStats.submittedTriangles += geometry.indexArrayLength / 3;
    gl.drawElements(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0);
  }

  private renderPoints(gl: WebGL2RenderingContext, object: HsGraphicPoints, camera: HsCamera): void {
    const isDebugPoints: boolean = object.name.startsWith('debug-');
    if (isDebugPoints) {
      gl.disable(gl.DEPTH_TEST);
      gl.depthMask(false);
    }
    object.setPointSizeFactor(gl, (isDebugPoints ? 1 : this.pointCloudSizeScale) / this.downsampleRate);
    const pointSamplingStep: number = isDebugPoints ? 1 : Math.max(1, Math.floor(this.pointCloudSamplingStep));
    const loadedCount: number = object.geometry.positionArrayLength / 3;
    const pointCount: number =
      isDebugPoints || object.drawCount === null ? loadedCount : Math.max(0, Math.min(loadedCount, object.drawCount));
    const pointStride: number = pointSamplingStep * HsGraphicConstants.XYZ_POINT_BYTES;
    gl.bindBuffer(gl.ARRAY_BUFFER, object.geometry.buffer);
    const positionsLocation: number = this.currentShader.getAttribute('a_positions');
    gl.vertexAttribPointer(positionsLocation, 3, gl.FLOAT, false, pointStride, 0);
    const colorsLocation: number = this.currentShader.getAttribute('a_pointColors');
    if (colorsLocation !== -1) {
      const offset: number = object.geometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      if (object.geometry.pointColorFormat === 'rgb8') {
        gl.vertexAttribPointer(colorsLocation, 3, gl.UNSIGNED_BYTE, true, pointSamplingStep * 3, offset);
      } else {
        gl.vertexAttribPointer(colorsLocation, 3, gl.FLOAT, false, pointStride, offset);
      }
    }
    this.currentShader.uniform3f(
      gl,
      'u_color',
      object.material.color.r,
      object.material.color.g,
      object.material.color.b
    );
    HsRender.#matrix4Temp2.copy(object.worldMatrix);
    HsRender.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRender.#matrix4Temp2.subtractPosition(HsRender.#vector3Temp1.values);
    this.currentShader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRender.#matrix4Temp2.values);
    this.frameStats.drawCalls++;
    if (this.tagPointPixels && !isDebugPoints) {
      // Write alpha 0.5 instead of the point's alpha; a later opaque mesh overwrites it back to 1.
      gl.blendColor(0, 0, 0, HS_POINT_PIXEL_TAG_ALPHA);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.CONSTANT_ALPHA, gl.ZERO);
      gl.drawArrays(gl.POINTS, 0, Math.ceil(pointCount / pointSamplingStep));
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.drawArrays(gl.POINTS, 0, Math.ceil(pointCount / pointSamplingStep));
    }
    if (isDebugPoints) {
      gl.depthMask(true);
      gl.enable(gl.DEPTH_TEST);
    }
  }

  private renderSprite(gl: WebGL2RenderingContext, object: HsGraphicSprite, camera: HsCamera): void {
    const shader: HsShader = object.material.getShader();
    shader.uniform3f(gl, 'u_color', object.material.color.r, object.material.color.g, object.material.color.b);
    const cameraPosition: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(camera.worldMatrix.values);
    const matrix: HsMatrix4 = HsMatrix4.makeCopy(object.worldMatrix);
    matrix.subtractPosition(cameraPosition.values);
    matrix.copyRotation(camera.worldMatrix);
    matrix.negateRotation();
    matrix.scale([object.scale, object.scale / object.aspect, 1]);
    matrix.addTransition([-object.center[0], -object.center[1], 0]);
    shader.uniformMatrix4fv(gl, 'u_relativeMatrix', matrix.values);

    if (object.material instanceof HsTextureMaterial) {
      const texture: WebGLTexture | null = object.material.texture;
      if (texture === null) {
        return;
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      const uvsLocation: number = this.currentShader.getAttribute('a_texcoord');
      if (uvsLocation === -1) {
        return;
      }
      this.currentShader.uniform4fv(gl, 'u_textureRect', object.material.textureRect);
      const offset: number = object.geometry.positionArrayLength * HsGraphicConstants.FLOAT32_BYTES;
      gl.vertexAttribPointer(uvsLocation, 2, gl.FLOAT, false, HsGraphicConstants.XY_POINT_BYTES, offset);
    }
    this.frameStats.drawCalls++;
    this.frameStats.submittedTriangles += HsSpriteConstants.SPRITE_INDICES.length / 3;
    gl.drawElements(gl.TRIANGLES, HsSpriteConstants.SPRITE_INDICES.length, gl.UNSIGNED_INT, 0);
  }

  private renderLines(gl: WebGL2RenderingContext, object: HsGraphicLines, camera: HsCamera): void {
    const shader: HsShader = object.material.getShader();
    const isDebugLine: boolean = object.name.startsWith('debug-');
    if (isDebugLine) {
      gl.disable(gl.DEPTH_TEST);
      gl.depthMask(false);
    }
    shader.uniform3f(gl, 'u_color', object.material.color.r, object.material.color.g, object.material.color.b);
    HsRender.#matrix4Temp2.copy(object.worldMatrix);
    HsRender.#vector3Temp1.setFromMatrixPosition(camera.worldMatrix.values);
    HsRender.#matrix4Temp2.subtractPosition(HsRender.#vector3Temp1.values);
    shader.uniformMatrix4fv(gl, 'u_relativeMatrix', HsRender.#matrix4Temp2.values);
    this.frameStats.drawCalls++;
    gl.drawArrays(gl.LINES, 0, object.geometry.positionArrayLength / 3);
    if (isDebugLine) {
      gl.depthMask(true);
      gl.enable(gl.DEPTH_TEST);
    }
  }

  private renderIndexedCollection(
    gl: WebGL2RenderingContext,
    geometry: HsIndexedGeometry,
    object: HsGraphicCollection,
    vertexArray: boolean = false
  ): void {
    const count: number = object.getCount();
    if (!vertexArray) {
      this.currentShader.selectInstanceMatrix4(gl, 'a_relativeMatrix', object.matrixBuffer);
      gl.bindBuffer(gl.ARRAY_BUFFER, object.colorBuffer);
      const colorAttribute: number = this.currentShader.getAttribute('a_color');
      if (colorAttribute !== -1) {
        gl.vertexAttribPointer(colorAttribute, 4, gl.FLOAT, false, 16, 0);
        this.currentShader.setAttributeInstanced(gl, colorAttribute, true);
      }
    }
    this.frameStats.drawCalls++;
    this.frameStats.instancedDrawCalls++;
    this.frameStats.instancedInstances += count;
    this.frameStats.submittedTriangles += (geometry.indexArrayLength / 3) * count;
    gl.drawElementsInstanced(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0, count);
  }

  private clearViewport(gl: WebGL2RenderingContext, alpha: number): void {
    gl.clearColor(this.clearColor.r, this.clearColor.g, this.clearColor.b, alpha);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.clear(gl.STENCIL_BUFFER_BIT);
  }

  private static isPlainMesh(object: HsGraphicObject): boolean {
    return !(
      object instanceof HsGraphicSprite ||
      object instanceof HsGraphicPoints ||
      object instanceof HsGraphicLines
    );
  }
}
